import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import Alert from '@mui/material/Alert';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import {
  SelectedPointOfInterestDocument,
  PointOfInterestsDocument,
  useUpdatePaintRegionIdMutation,
} from '@/__generated__/graphql';

export type UseUpdatePaintRegionIdHookType = {
  isRetrying: boolean;
  handleAddPaintRegionId: () => void;
};

export const useUpdatePaintRegionId = (
  pointOfInterestIds: string[],
  type?: string
): UseUpdatePaintRegionIdHookType => {
  const structureId = useGetStructureId();
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const setApplyFilters = useSetAtom(state.applyFilters);
  const setRefetchUpdatedPois = useSetAtom(state.refetchUpdatedPois);
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);

  const [updatePaintRegionId, { loading: isRetrying }] = useUpdatePaintRegionIdMutation({
    refetchQueries: [PointOfInterestsDocument, SelectedPointOfInterestDocument],
    onCompleted: (response) => {
      setApplyFilters(true);
      setRefetchUpdatedPois(true);
      if (response?.updatePaintRegionId) {
        // Update the selected paint region IDs with the new type
        setSelectedPaintRegionIds((prevIds: string[]) => {
          if (!type) return prevIds;
          return [...prevIds, type];
        });
        
        setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="success" variant="filled">
              {`The Paint Block has been successfully added to the ${pointOfInterestIds.length > 1 ? 'POIs' : 'POI'}`}
            </Alert>
          ),
        });
      }
    },
    onError: (error: Error) => {
      setSnackbarMessage({
        shouldShow: true,
        content: (
          <Alert severity="error" variant="filled">
            {error.message}
          </Alert>
        ),
      });
      console.error(error);
    },
  });

  const handleAddPaintRegionId = useCallback(() => {
    if (pointOfInterestIds?.length > 0) {
      updatePaintRegionId({
        variables: {
          input: {
            structureId,
            poiIds: pointOfInterestIds,
            type: type,
          },
        },
      });
    }
  }, [pointOfInterestIds, structureId, type, updatePaintRegionId]);

  return { isRetrying, handleAddPaintRegionId };
};
